import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { useApp } from './context/AppContext';
import './styles/globals.css';
import './styles/landing-redesign.css';

// Components
import Layout from './components/Layout';
import LandingRedesign from './pages/LandingRedesign';
import CoachDashboard from './pages/CoachDashboard';
import HeadteacherDashboard from './pages/HeadMasterDashboard';
import AdminDashboard from './pages/AdminDashboard';
import Registration from './pages/Registration';
import Reports from './pages/Reports';
import NewRegistrations from './pages/NewRegistrations';
import TrackEventsRedesign from './pages/TrackEventsRedesign';
import FieldEventsRedesign from './pages/FieldEventsRedesign';
import CoachAccount from './pages/CoachAccount';
import HeadteacherAccount from './pages/HeadteacherAccount';

// Protected Route Component
const ProtectedRoute: React.FC<{ 
  children: React.ReactNode; 
  allowedRoles?: string[] 
}> = ({ children, allowedRoles = [] }) => {
  const { user } = useApp();
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

// Main App Routes Component
const AppRoutes: React.FC = () => {
  const { user, isLoading } = useApp();
  
  // Show loading overlay during app initialization or authentication
  if (isLoading && !user) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, #0A0E17 0%, #111827 50%, #0A0E17 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2rem',
        zIndex: 9999
      }}>
        <div style={{
          position: 'relative',
          width: '80px',
          height: '80px'
        }}>
          {/* Outer ring */}
          <div style={{
            position: 'absolute',
            width: '80px',
            height: '80px',
            border: '3px solid rgba(212, 175, 55, 0.1)',
            borderTop: '3px solid #FFD700',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          {/* Inner ring */}
          <div style={{
            position: 'absolute',
            top: '15px',
            left: '15px',
            width: '50px',
            height: '50px',
            border: '3px solid rgba(196, 30, 58, 0.1)',
            borderBottom: '3px solid #C41E3A',
            borderRadius: '50%',
            animation: 'spinReverse 0.8s linear infinite'
          }}></div>
          {/* Center dot */}
          <div style={{
            position: 'absolute',
            top: '35px',
            left: '35px',
            width: '10px',
            height: '10px',
            background: 'linear-gradient(135deg, #FFD700, #D4AF37)',
            borderRadius: '50%',
            animation: 'pulse 1.5s ease-in-out infinite'
          }}></div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{
            margin: 0,
            fontSize: '1.75rem',
            fontFamily: "'Bebas Neue', sans-serif",
            letterSpacing: '3px',
            background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 50%, #FFD700 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textTransform: 'uppercase'
          }}>
            Athletics Registration
          </h2>
          <p style={{
            margin: '0.75rem 0 0 0',
            color: '#9CA3AF',
            fontSize: '0.95rem',
            letterSpacing: '0.5px'
          }}>
            Initializing system...
          </p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes spinReverse {
            0% { transform: rotate(360deg); }
            100% { transform: rotate(0deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(0.8); }
          }
        `}</style>
      </div>
    );
  }
  
  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route 
          path="/" 
          element={user ? <Navigate to="/dashboard" replace /> : <LandingRedesign />} 
        />
        
        <Route path="/track-events" element={<TrackEventsRedesign />} />
        <Route path="/field-events" element={<FieldEventsRedesign />} />
        
        {/* Protected Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Layout>
              {user?.role === 'admin' ? (
                <AdminDashboard />
              ) : user?.role === 'coach' ? (
                <CoachDashboard />
              ) : (
                <HeadteacherDashboard />
              )}
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/register" element={
          <ProtectedRoute allowedRoles={['coach']}>
            <Layout>
              <Registration />
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/reports" element={
          <ProtectedRoute>
            <Layout>
              <Reports />
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/new-registrations" element={
          <ProtectedRoute>
            <Layout>
              <NewRegistrations />
            </Layout>
          </ProtectedRoute>
        } />
        
        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout>
              <AdminDashboard />
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/admin/users" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout>
              <AdminDashboard activeTab="users" />
            </Layout>
          </ProtectedRoute>
        } />
        
        <Route path="/admin/registrations" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout>
              <AdminDashboard activeTab="registrations" />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/admin/schools" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout>
              <AdminDashboard activeTab="schools" />
            </Layout>
          </ProtectedRoute>
        } />

        <Route path="/admin/account" element={
          <ProtectedRoute allowedRoles={['admin']}>
            <Layout>
              <AdminDashboard activeTab={"account" as any} />
            </Layout>
          </ProtectedRoute>
        } />

        {/* Account Management Routes for all user types */}
        <Route path="/account" element={
          <ProtectedRoute>
            <Layout>
              {user?.role === 'coach' ? (
                <CoachAccount />
              ) : user?.role === 'headteacher' ? (
                <HeadteacherAccount />
              ) : (
                <CoachAccount />
              )}
            </Layout>
          </ProtectedRoute>
        } />
        
        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <AppProvider>
      <div className="App">
        <AppRoutes />
      </div>
    </AppProvider>
  );
};

export default App;
